/* eslint-disable @typescript-eslint/naming-convention */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { HotToastService } from '@ngneat/hot-toast';
import { badgeColorCode, BadgeOptions } from 'app/components/badge/badge.interface';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { LocalisationService } from 'app/core/services/localisation/zuper.localisation.service';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import { PickerSelectOptions } from '../data-picker.interface';
import _ from 'lodash';
import { MultiModulePickerService } from 'app/components/multi-module-picker/multi-module-picker.service';

@Component({
  selector: 'project-data-picker',
  templateUrl: './project-data-picker.component.html',
  styleUrls: ['./project-data-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectDataPickerComponent implements OnInit, OnChanges {
  @Input() filter: any = null;
  @Input() selectOption: PickerSelectOptions = PickerSelectOptions.select;
  @Output() eventEmitter: EventEmitter<any> = new EventEmitter();
  @Input() selectedProjects: any[] = [];
  @Input() searchKey: string = '';
  @Input() pickerConfig: any = null;
  @Input() projectUrl: any = '';
  @Input() pageIndex: number = 0;
  @Input() showSelectedItems: boolean = false;
  @Input() sortBy: string = 'created_at';
  @Input() sortType: string = 'DESC';
  @Input() projectData: any[] = null;
  @Input() customParams: any;
  @Input() filterFormGroup: FormGroup;
  @Input() hasFilterValue: boolean = false;
  @Input() associatedProjectsArr: any = null;
  @Input() isFromNote: boolean = false;
  @Input() module: string = null;
  @Input() moduleUID: string = null;

  duplicatedProjectData: any[] = [];
  projects: any[] = [];
  projectsList: any[] = [];
  loaded: boolean = false;
  defaultParamObj: any = {
    'sort': this.sortType,
    'sort_by': this.sortBy,
    'filter.is_active': true
  };
  projectEleId: string;
  projectDetails: any = null;
  onSearch: boolean = true;
  localeKeys: any = {
    expand: 'expand',
    collapse: 'collapse'
  };
  isSpinLoading: boolean = false;
  timeZone: any;
  isAssociatedExpanded: boolean = true;
  notesPickerPreferences: any = {};
  associatedProjects: any[] = [];

  constructor(
    private utilityService: UtilityService,
    private dataCollectorService: DataCollectorService,
    private localisationService: LocalisationService,
    private changeDetectorRef: ChangeDetectorRef,
    private multiModulePickerService: MultiModulePickerService,
    private toast: HotToastService) { }

  ngOnInit(): void {
    this.notesPickerPreferences = this.multiModulePickerService.notesPickerPreferences;
    this.localisationService.setDefaultLanguage();
    this.timeZone = this.timeZone ?? this.utilityService.timeZone;
    if (this.selectOption && this.selectOption === PickerSelectOptions.readOnly) {
      delete this.defaultParamObj?.['filter.is_active'];
    }
    this.filterFormGroup?.valueChanges.subscribe((data: any) => {
      this.updateDataParams(data);
    });
    this.showDataPickerPanel();
  }

  getProjectStatusOptions(status: any): BadgeOptions {
    const badgeOption: BadgeOptions = {
      type: status?.project_status_name,
      color: status?.project_status_color || badgeColorCode[status?.project_status_type],
      displayName: status?.project_status_name,
      extraClass: 'capitalize'
    };
    return badgeOption;
  }

  getTagBadgeOptions(tag: string): BadgeOptions {
    const badgeOption: BadgeOptions = {
      type: tag,
      displayType: tag,
      color: badgeColorCode[tag],
      displayKey: tag,
      displayName: tag
    };
    return badgeOption;
  }

  rotateArrowIcon(projectUID: any, isAssociated?: boolean): any {
    const isExpanded: boolean = this.isExpandingAction(projectUID, isAssociated);
    return {
      'rotate-arrow-icon-down': isExpanded,
      'rotate-arrow-icon-up': !isExpanded
    };
  }

  showSpinLoading(projectUID: any, isAssociated?: boolean): boolean {
    return this.isExpandingAction(projectUID, isAssociated) && this.isSpinLoading;
  }

  updateArrowTooltipText(projectUID: any, isAssociated?: boolean): string {
    return this.isExpandingAction(projectUID, isAssociated) ? this.localeKeys.collapse : this.localeKeys.expand;
  }

  isExpandingAction(projectUID: any, isAssociated?: boolean): boolean {
    return (this.projectEleId === 'project_details_' + projectUID && !isAssociated) || (this.projectEleId === 'project_details_associated_' + projectUID && isAssociated);
  }

  viewProjectFullDetails(projectUID: string): string {
    return '/projects/' + projectUID + '/details';
  }

  viewProjectDetails(event: any, projectUID: string, isAssociated?: boolean): void {
    event.stopPropagation();
    const elementId: string = (isAssociated ? 'project_details_associated_' : 'project_details_') + projectUID;
    if (this.projectEleId === elementId) {
      this.resetProjectDetails();
    } else {
      this.projectEleId = elementId;
      this.getProjectDetails(projectUID);
    }
  }

  getProjectDetails(projectUID: string): void {
    this.isSpinLoading = true;
    /* eslint-disable quotes */
    this.dataCollectorService.getData(this.projectUrl + `/` + projectUID).subscribe(
      /* eslint-enable quotes */
      (projects: any) => {
        this.projectDetails = projects.data;
        this.isSpinLoading = false;
        this.changeDetectorRef.detectChanges();
      }, (_error: any) => {
        this.resetProjectDetails();
        this.isSpinLoading = false;
      });
  }

  resetProjectDetails(): void {
    this.projectEleId = '';
    this.projectDetails = null;
    this.changeDetectorRef.detectChanges();
  }

  isSelectedProject(project: any): boolean {
    if (this.selectedProjects?.length) {
      return this.selectedProjects.find(value => value.project_uid === project.project_uid) ? true : false;
    }
    return false;
  }

  selectProject(event: any, project: any): void {
    if (this.selectOption === PickerSelectOptions.multiSelect) {
      if (event?.target?.checked === true) {
        this.selectedProjects.push(project);
      } else {
        this.selectedProjects = this.selectedProjects.filter(value => value.project_uid !== project.project_uid);
      }
    } else {
      this.selectedProjects = [project];
    }
    this.eventEmitter.emit({ value: this.selectedProjects });
  }

  triggerSearch(searchKey: any): void {
    this.onSearch = true;
    if (this.projectData) {
      this.projectDataSearch(searchKey);
    } else {
      this.projectRemoteDataSearch(searchKey);
    }
  }

  projectDataSearch(searchKey: any): void {
    if (!searchKey) {
      this.projects = this.duplicatedProjectData;
    } else {
      const filteredValue: any[] = this.duplicatedProjectData?.filter((project: any) => {
        searchKey = searchKey.toLowerCase();
        const projectId: string = project?.project_id?.toString()?.toLowerCase();
        const projectTitle: string = project?.project_name?.toString()?.toLowerCase();
        const projectDescription: string = project?.project_description?.toString()?.toLowerCase();
        if (projectId?.indexOf(searchKey) > -1 || projectTitle?.indexOf(searchKey) > -1 || projectDescription?.indexOf(searchKey) > -1) {
          return project;
        }
      });
      this.projects = filteredValue?.length ? [...filteredValue] : [];
    }
  }

  projectRemoteDataSearch(searchKey: any): void {
    delete this.defaultParamObj[this.pickerConfig.params.uniqueSearchKey];
    if (this.defaultParamObj[this.pickerConfig.params.searchKey] !== searchKey) {
      const pageIndex: number = 0;
      this.defaultParamObj[this.pickerConfig.params.searchKey] = searchKey;
      this.getData(pageIndex);
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
  }

  sortingAction(key: string, value: string): void {
    if (value) {
      const pageIndex: number = this.pageIndex ?? 0;
      this.defaultParamObj[key] = value;
      this.getData(pageIndex);
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
  }

  getData(pageIndex: number, isOpen?: boolean): void {
    this.projectEleId = '';
    this.loaded = false;
    this.defaultParamObj.page = pageIndex + 1;
    const urlParams = this.utilityService.constructParam(this.defaultParamObj);
    if(this.notesPickerPreferences[this.module]?.preference?.project === 'LINKED'){
      urlParams.updates.push({
        "param": this.module === 'ORGANIZATION' ? 'filter.customer_organization' : this.notesPickerPreferences[this.module]?.filter,
        "value": this.moduleUID,
        "op": "s"
      });
    } else if(this.notesPickerPreferences[this.module]?.preference?.project === 'BOTH' && this.isFromNote){
        this.updateAssociatedData();
    }
    this.dataCollectorService.getData(this.projectUrl, urlParams).subscribe(
      (response: any) => {
        this.projectsList = response.data;
        this.projects = response.data;
        this.loaded = true;
        this.eventEmitter.emit({ totalRecords: response.total_records });
        if (isOpen && this.filter && this.projects.length) {
          this.selectedProjects = this.selectedProjects?.length > 0 ? _.uniqBy([...this.selectedProjects, ...this.filter], 'project_uid') : this.filter;
          this.eventEmitter.emit({ value: this.selectedProjects });
        } else if (isOpen && this.projects.length === 0) {
          this.onSearch = false;
        }
        this.changeDetectorRef.detectChanges();
      }, (_error: any) => {
        this.loaded = true;
      });
  }

  getAssociatedData(urlParams): void {
    const key = this.module === 'ORGANIZATION' ? 'filter.customer_organization' : this.notesPickerPreferences[this.module]?.filter
    urlParams[key] = this.moduleUID;
    this.dataCollectorService.getData(this.projectUrl, urlParams).subscribe(
      (response: any) => {
        this.associatedProjects = response.data;
        this.changeDetectorRef.detectChanges();
      }, (_error: any) => {
      });
  }

  updateAssociatedData(): any{
    if(this.associatedProjectsArr?.length > 0 || this.module === 'JOB'){
      const keyword = this.defaultParamObj['filter.keyword']?.toLowerCase() || '';
      this.associatedProjects = this.associatedProjectsArr?.filter(project =>
        Object.values(project).some(value =>
          (typeof value === 'string' || typeof value === 'number') &&
          value.toString().toLowerCase().includes(keyword)
        )
      );
    } else{
      const urlParams =  {
        'sort': 'DESC',
        'sort_by': 'created_at',
        'filter.is_active': true,
        'page': 1,
        'count': 20,
        'filter.keyword':  this.defaultParamObj['filter.keyword'],
      };
      this.getAssociatedData(urlParams);
    }
  }

  showDataPickerContent(): void {
    if (this.hasFilterValue) {
      return null;
    }
    const pageIndex: number = this.pageIndex ?? 0;
    Object.keys(this.pickerConfig.params).forEach((key: any) => {
      const paramKey: any = this.pickerConfig.params[key];
      if (key !== 'uniqueSearchKey') {
        let value: any = '';
        if (key === 'countKey') {
          value = 10;
        } else if (key === 'pageKey') {
          value = 1;
        } else if (key === 'searchKey' && !this.showSelectedItems && this.searchKey) {
          value = this.searchKey;
        }
        this.defaultParamObj[paramKey] = value;
      }
    });
    if (this.customParams && Object.keys(this.customParams).length) {
      this.defaultParamObj = { ...this.defaultParamObj, ...this.customParams };
    }
    if (this.filter) {
      this.updateFilterValue();
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
    this.eventEmitter.emit({ sortBy: this.defaultParamObj['sort_by'], sortType: this.defaultParamObj['sort'] });
    this.getData(pageIndex, true);
  }

  updateFilterValue(): void {
    if (this.selectOption === PickerSelectOptions.select && this.filter.length > 0) {
      if (this.filter[0].project_name) {
        this.defaultParamObj[this.pickerConfig.params.searchKey] = this.filter[0].project_name;
        this.eventEmitter.emit({ searchKey: this.filter[0].project_name });
      } else {
        this.defaultParamObj[this.pickerConfig.params.uniqueSearchKey] = this.filter[0].project_uid;
      }
    }
  }

  updateDataParams(formControlValues: any): void {
    if (this.customParams && Object.keys(this.customParams).length) {
      this.defaultParamObj = { ...this.defaultParamObj, ...this.customParams };
    }
    const params: any = this.defaultParamObj ?? {};
    if (formControlValues.moduleBasedFilterValue) {
      formControlValues.moduleBasedFilterValue.forEach((value: any) => {
        if (value.value) {
          params[value?.key] = value.value;
        } else {
          delete params[value?.key];
        }
      });
    }
    this.updateFilters();
  }

  updateFilters(): void {
    const pageIndex: number = 0;
    this.getData(pageIndex, true);
    this.eventEmitter.emit({ pageIndex: pageIndex });
  }

  updateProjectData(): void {
    this.projects = this.projectData;
    this.duplicatedProjectData = [...this.projectData];
    this.onSearch = false;
    this.loaded = true;
    if (this.filter && this.projectData?.length) {
      this.selectedProjects = this.filter;
      this.eventEmitter.emit({ value: this.selectedProjects });
    }
    if (this.searchKey) {
      this.eventEmitter.emit({ searchKey: this.searchKey });
      this.triggerSearch(this.searchKey);
    }
  }

  resetProjectDataPicker(): void {
    this.projects = [];
  }

  showDataPickerPanel(): void {
    this.resetProjectDataPicker();
    if (this.projectData) {
      this.updateProjectData();
    } else if (this.showSelectedItems) {
      this.projects = this.selectedProjects;
    } else if (this.projectsList.length === 0 && this.searchKey) {
      this.showDataPickerContent();
    } else if (this.projectsList.length > 0 || this.searchKey) {
      this.projects = this.projectsList;
    } else {
      this.showDataPickerContent();
    }
  }

  hideSelectedItemsPanel(): void {
    this.eventEmitter.emit({ showSelectedItems: false, value: this.selectedProjects, filter: this.selectedProjects });
  }

  getBadgeOptions(): BadgeOptions {
    return {
      type: 'PRIMARY',
      displayKey: null,
      displayName: this.selectedProjects ? this.selectedProjects.length : 0,
      displayType: 'listingCount'
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.searchKey?.firstChange === false) {
      this.triggerSearch(changes?.searchKey?.currentValue);
    }

    if (changes?.pageIndex?.previousValue !== undefined) {
      this.isAssociatedExpanded = false;
      this.getData(changes?.pageIndex?.currentValue);
    }

    if (changes?.showSelectedItems?.firstChange === false || changes?.projectData?.firstChange === false) {
      this.showDataPickerPanel();
    }

    if (changes?.sortBy?.firstChange === false && changes?.sortBy?.previousValue !== undefined) {
      this.sortingAction('sort_by', changes?.sortBy?.currentValue);
    }

    if (changes?.sortType?.firstChange === false && changes?.sortType?.previousValue !== undefined) {
      this.sortingAction('sort', changes?.sortType?.currentValue);
    }
  }
}
